/* PageHeader — Phase B4 layout primitive.
 *
 * The header strip that sits at the top of a Page's content area. Three
 * meaningful slots:
 *   - title    (required) — string or composed node (h1 by default)
 *   - subtitle (optional) — caption/description line under the title
 *   - meta     (optional) — small status line (date, sync indicator)
 *   - actions  (optional) — right-aligned cluster (buttons, segmented)
 *
 * Designed for ForecastItaly's brand row graduation: title carries the
 * country label, meta carries the date + savedAt + HubSpot sync indicator,
 * actions carries the cluster of utility buttons + lang segmented. But
 * it's deliberately generic — UserProfile can use it for the user's name,
 * AdminUsers for the "Users" h1 + admin-unlock affordance, etc.
 *
 * The visual hierarchy:
 *   1. row: title  ····  actions          (top, equal-baseline)
 *   2. row: subtitle                       (left, beneath title)
 *   3. row: meta                            (left, smaller, muted)
 *
 * Rendered as <header> for semantics. Section.tsx's spacing rule
 * (margin-bottom: --space-6) doesn't apply — PageHeader uses --space-5
 * by default so it sits closer to the first content section than
 * Sections do to each other.
 */
import React from "react";

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  /** Render the title as an h2 instead of h1 — for nested pages where
   *  the topbar already carries the page h1 semantically. Defaults to
   *  h1 so the markup reads correctly when used standalone. */
  level?: 1 | 2;
  className?: string;
}

export default function PageHeader({
  title,
  subtitle,
  meta,
  actions,
  level = 1,
  className = "",
}: PageHeaderProps) {
  const TitleTag = level === 2 ? "h2" : "h1";
  return (
    <header className={`v2-page-header ${className}`.trim()}>
      <div className="v2-page-header__top">
        <TitleTag className="v2-page-header__title">{title}</TitleTag>
        {actions ? <div className="v2-page-header__actions">{actions}</div> : null}
      </div>
      {subtitle ? <div className="v2-page-header__subtitle">{subtitle}</div> : null}
      {meta ? <div className="v2-page-header__meta">{meta}</div> : null}
    </header>
  );
}
